import { useMutation, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useArchiveRepository, useReportRepository } from '../container';
import { keys } from '../query-keys';
import type {
  ArchiveFilter,
  ArchivePage,
  DeletedCaseFilter,
  DeletedCasePage,
} from '../ports/ArchiveRepository';
import type { ReportRange, SalesFilter } from '../ports/ReportRepository';

export interface ArchiveQueryOptions {
  /** Off for a caller who may not see the set, so they never ask for it. */
  enabled?: boolean;
}

/**
 * The archive, either of its sets.
 *
 * The set travels in the filter, and the filter is the query key, so the
 * Delivered and Deleted tabs cache side by side and anything that invalidates
 * the archive refetches both.
 */
export function useArchive(
  filter: DeletedCaseFilter,
  options?: ArchiveQueryOptions,
): UseQueryResult<DeletedCasePage>;
export function useArchive(
  filter: ArchiveFilter,
  options?: ArchiveQueryOptions,
): UseQueryResult<ArchivePage>;
export function useArchive(
  filter: ArchiveFilter | DeletedCaseFilter,
  { enabled = true }: ArchiveQueryOptions = {},
): UseQueryResult<ArchivePage | DeletedCasePage> {
  const repo = useArchiveRepository();
  const deleted = filter.set === 'deleted';
  return useQuery<ArchivePage | DeletedCasePage>({
    queryKey: keys.archive(filter),
    queryFn: () => (filter.set === 'deleted' ? repo.deleted(filter) : repo.list(filter)),
    enabled,
    // Finished business does not change; only the filter does. A deletion
    // waiting for somebody's decision is not finished business.
    staleTime: deleted ? 0 : 60_000,
    // Keeps the previous page on screen while a new filter loads, so typing in
    // the search box does not flash an empty table on every keystroke.
    placeholderData: (previous) => previous,
  });
}

export function useCaseRecord(jobOrderNo: string | undefined) {
  const repo = useArchiveRepository();
  return useQuery({
    queryKey: [...keys.archive({}), 'record', jobOrderNo ?? ''],
    queryFn: () => repo.record(jobOrderNo as string),
    enabled: jobOrderNo !== undefined,
  });
}

export function useArchiveExport() {
  const repo = useArchiveRepository();
  return useMutation({
    mutationFn: (filter: ArchiveFilter) => repo.exportCsv(filter),
  });
}

export function useReportSummary(range: ReportRange) {
  const repo = useReportRepository();
  return useQuery({
    queryKey: keys.reports('summary', range),
    queryFn: () => repo.summary(range),
    staleTime: 30_000,
  });
}

export function useSalesReport(filter: SalesFilter, enabled = true) {
  const repo = useReportRepository();
  return useQuery({
    queryKey: keys.reports('sales', filter.range, filter),
    queryFn: () => repo.sales(filter),
    // Not asked on behalf of a reader the server would refuse: sales are money.
    enabled,
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  });
}

/**
 * The change against the previous period, as a whole percentage.
 *
 * Null when there is nothing to compare against: a first week with no previous
 * week is not a hundred percent rise, and showing one would be a number the
 * client would reasonably query.
 */
export function percentChange(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return Math.round(((current - previous) / previous) * 100);
}
